import { PlayerRef } from "@remotion/player";
import { useCallback, useEffect, useState, useRef } from "react";

export const useTimelinePosition = ({
  playerRef,
}: {
  playerRef: React.RefObject<PlayerRef | null>;
}) => {
  const [frame, setFrame] = useState(0);
  const requestRef = useRef<number>();
  const previousTimeRef = useRef<number>();

  const animate = useCallback(
    (time: number) => {
      if (previousTimeRef.current === undefined) {
        previousTimeRef.current = time;
      }

      if (playerRef.current) {
        setFrame(playerRef.current.getCurrentFrame());
      }

      requestRef.current = requestAnimationFrame(animate);
    },
    [playerRef]
  );

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);

    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [animate]);

  useEffect(() => {
    const { current } = playerRef;
    if (!current) return;

    const handleStateChange = () => {
      if (current) {
        setFrame(current.getCurrentFrame());
      }
    };

    current.addEventListener("play", handleStateChange);
    current.addEventListener("pause", handleStateChange);
    current.addEventListener("seeked", handleStateChange);

    return () => {
      if (current) {
        current.removeEventListener("play", handleStateChange);
        current.removeEventListener("pause", handleStateChange);
        current.removeEventListener("seeked", handleStateChange);
      }
    };
  }, [playerRef]);

  return frame;
};
